import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import './Analysis.css';
import notePreferencesImage from './../assets/note-preferences.png';
import ClipLoader from 'react-spinners/ClipLoader';
import PdfViewer from '../components/PdfViewer';
import CONFIG from '../config.js';

const Analysis = () => {
    const { user, fetchData, setFetchData, isAdmin, adminSelectedUser } = useContext(AuthContext);
    let userInfo = user;
    if (isAdmin) {
        userInfo = adminSelectedUser;
    }
    const [copiedIndex, setCopiedIndex] = useState(null);
    const startDatePickerRef = useRef(null);
    const endDatePickerRef = useRef(null);
    const [showStartDateInput, setShowStartDateInput] = useState(false);
    const [showEndDateInput, setShowEndDateInput] = useState(false);
    const notePreferencesModalRef = useRef(null);
    const askAIModalRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAskAIModalOpen, setIsAskAIModalOpen] = useState(false);
    const [askAIInput, setAskAIInput] = useState('');
    const [aiAnswer, setAiAnswer] = useState();
    const [aiError, setAiError] = useState('');
    const [aiAnswerLoading, setAiAnswerLoading] = useState(false);
    const formatDate = (date) => {

        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    }

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [rightActiveTab, setRightActiveTab] = useState(0);
    const [endDate, setEndDate] = useState();
    const [sessionEndDate, setSessionEndDate] = useState();
    const [startDate, setStartDate] = useState();
    const [sessionStartDate, setSessionStartDate] = useState();
    const [reportData, setReportData] = useState();
    const [pdfUrl, setPdfUrl] = useState(null);
    const [maxDate, setMaxDate] = useState();
    const analysisSummaryTitle = "\ud83e\udde0 HRV Summary Report";
    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    const fetchReportData = async (startDate, endDate) => {
        try {
            let reportEndDate = endDate;
            let reportStartDate = startDate;
            if(!endDate) {
                const maxDateResponse = await axios.get(`${url}hrv/predictions/max-day?uuid=${userInfo.uuid}`, config);
                const reportMaxDay = maxDateResponse.data.max_day;
                
                const maxDayDatePlusOne = new Date(reportMaxDay);
                maxDayDatePlusOne.setDate(maxDayDatePlusOne.getDate() + 1);
                const maxSelectableDate = formatDate(maxDayDatePlusOne);

                setMaxDate(maxSelectableDate);
                setFetchData(prev => ({ ...prev, maxDate: reportMaxDay }));
                
                const maxDayDate = new Date(reportMaxDay);
                
                const displayStart = new Date(maxDayDate);
                displayStart.setDate(displayStart.getDate() - 12);
                const displayEnd = new Date(maxDayDate);
                displayEnd.setDate(displayEnd.getDate() + 1);

                const reportStart = new Date(maxDayDate);
                reportStart.setDate(reportStart.getDate() - 13);
                const reportEnd = new Date(maxDayDate);
                
                const displayStartStr = formatDate(displayStart);
                const displayEndStr = formatDate(displayEnd);
                const reportStartStr = formatDate(reportStart);
                const reportEndStr = formatDate(reportEnd);
                
                setStartDate(displayStartStr);
                setEndDate(displayEndStr);
                setSessionStartDate(displayStartStr);
                setSessionEndDate(displayEndStr);

                reportStartDate = reportStartStr;
                reportEndDate = reportEndStr;
            }
            
            const [reportData, pdfData] = await axios.all([
                axios.get(`${url}report/generate?uuid=${userInfo.uuid}&start_date=${reportStartDate}&end_date=${reportEndDate}`, config),
                axios.get(`${url}report/generate/pdf?uuid=${userInfo.uuid}&start_date=${reportStartDate}&end_date=${reportEndDate}`, {
                    responseType: 'blob',
                }),
            ]);
            setReportData(reportData.data);
            const pdfDataUrl = URL.createObjectURL(pdfData.data);
            setPdfUrl(pdfDataUrl);
            setFetchData(prev => ({ ...prev, analysisReportData: reportData.data, analysisStartDate: reportStartDate, analysisEndDate: reportEndDate, analysisPdfDataURL: pdfDataUrl }));
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!fetchData.analysisReportData || !fetchData.analysisPdfDataURL) {
            fetchReportData();
        } else {
            setReportData(fetchData.analysisReportData);
            setStartDate(fetchData.analysisStartDate);
            setEndDate(fetchData.analysisEndDate);
            setSessionStartDate(fetchData.analysisStartDate);
            setSessionEndDate(fetchData.analysisEndDate);
            setPdfUrl(fetchData.analysisPdfDataURL);
            setMaxDate(fetchData.maxDate);
            setLoading(false);
        }
    }, []);

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    }

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    }

    const generateReport = () => {
        setLoading(true);
        const adjustedStartDate = formatDate(new Date(new Date(startDate).setDate(new Date(startDate).getDate() - 1)));
        const adjustedEndDate = formatDate(new Date(new Date(endDate).setDate(new Date(endDate).getDate() - 1)));
        setSessionStartDate(startDate);
        setSessionEndDate(endDate);
        setShowStartDateInput(false);
        setShowEndDateInput(false);
        fetchReportData(adjustedStartDate, adjustedEndDate);
    }

    const onCancelBtnClick = () => {
        setShowStartDateInput(false);
        setShowEndDateInput(false);
        setStartDate(sessionStartDate);
        setEndDate(sessionEndDate);
    }

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedIndex(index);
                setTimeout(() => {
                    setCopiedIndex(null); // Hide tooltip after 2 seconds
                }, 2000);
            })
            .catch((err) => {
                console.error('Failed to copy text: ', err);
            });
    };

    function getDuration() {
        const start = new Date(sessionStartDate);
        const end = new Date(sessionEndDate);

        const differenceInMilliseconds = end - start;
        const millisecondsInADay = 1000 * 60 * 60 * 24;
        const differenceInDays = Math.round(differenceInMilliseconds / millisecondsInADay);

        return differenceInDays+1;
    }

    const onClickEdit = (dateType) => {
        if (dateType === 'startDate') {
            setShowStartDateInput(true);
        } else {
            setShowEndDateInput(true);
        }
    }

    const openModal = () => {
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll
    };

    const openAskAIModal = () => {
        setIsAskAIModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll
    }

    const closeModal = () => {
        setIsModalOpen(false);
        setIsAskAIModalOpen(false);
        setAiAnswer();
        setAskAIInput();
        document.body.style.overflow = 'auto'; // Restore body scroll
    };

    const handleClickOutside = (event) => {
        if (notePreferencesModalRef.current && !notePreferencesModalRef.current.contains(event.target)) {
            closeModal();
        }
        if (askAIModalRef.current && !askAIModalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const formatBoldText = (text) => {
        const parts = text.split(/(\*\*.*?\*\*)/g); // Split on **bolded text**
        
        return parts.map((part, index) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <b key={index}>{part.slice(2, -2)}</b>
          ) : (
            part
          )
        );
    };

    const formatString = (text) => {
        const formattedString = text.startsWith("- ") ? text.slice(2) : text;

        return formatBoldText(formattedString);
    }

    const onInputKeyDown = (event) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            onAskAI();
        }
    }

    const onAskAI = async () => {
        try {
            setAiAnswerLoading(true);
            setAiError(''); // clear previous error
            setAiAnswer();  // clear previous answer
            const response = await axios.get(`${url}question/answer?uuid=${userInfo.uuid}&start_date=${startDate}&end_date=${endDate}&question=${askAIInput}`, config);
            const formattedAnswer = response.data.answer.split('\n');
            setAiAnswer(formattedAnswer);
        } catch (err) {
            console.log(err);
            if (err.response && err.response.status === 400) {
                setAiError('The submitted query is unrelated to your Oura data. Please try again.');
            } else {
                setAiError('Something went wrong. Please try again.');
            }
        } finally {
            setAiAnswerLoading(false);
        }
    }

    return (
        <div className="container analysis-page">
            <div className="main-content">
                <section className="card">
                    <div className="action-container">
                        <div className="analysis-left-column">
                            <div className="overview-container">
                                <div className="overview-header">Overview</div>
                                <div className="overview-item"><b>User: </b>{userInfo.displayName ? userInfo.displayName : ''}</div>
                                <div className="overview-item">
                                    <label htmlFor="startDate"><b>Start Date:</b></label>
                                    {!showStartDateInput &&
                                        <>{sessionStartDate} <span className="blue-button" onClick={() => onClickEdit('startDate')}>Edit</span></>
                                    }
                                    {showStartDateInput &&
                                        <input className="date-input" ref={startDatePickerRef} type="date" id="startDate" name="startDate" max={maxDate} value={startDate} onChange={handleStartDateChange}></input>
                                    }
                                </div>
                                <div className="overview-item">
                                    <label htmlFor="endDate"><b>End Date:</b></label>
                                    {!showEndDateInput &&
                                        <>{sessionEndDate} <span className="blue-button" onClick={() => onClickEdit('endDate')}>Edit</span></>
                                    }
                                    {showEndDateInput &&
                                        <input className="date-input" ref={endDatePickerRef} type="date" id="endDate" name="endDate" max={maxDate} value={endDate} onChange={handleEndDateChange}></input>
                                    }
                                </div>
                                <div className="overview-item"><b>Duration:</b> {getDuration()} days</div>
                                {(showEndDateInput || showStartDateInput) &&
                                    <div className="generate-button-container">
                                        <span className="blue-button" onClick={generateReport}>Generate Report</span>
                                        <span className="blue-button" onClick={onCancelBtnClick}>Cancel</span>
                                    </div>
                                }
                            </div>
                            <div className="feature-request-container">
                                <div className="bulb-icon-container">
                                    <div className="bulb-icon"></div>
                                </div>
                                <div>
                                    <div>Have a feature request?</div>
                                    <div className="submit-btn" onClick={() => alert('Coming Soon in v2!')}>Submit your idea</div>
                                </div>
                            </div>
                        </div>
                        <div className="analysis-mid-column">
                            <div className="analysis-tabs">
                                <button
                                    className={activeTab === 0 ? 'active' : ''}
                                    onClick={() => setActiveTab(0)}
                                >
                                    Detailed Analysis
                                </button>
                                <button
                                    className={activeTab === 1 ? 'active' : ''}
                                    onClick={() => setActiveTab(1)}
                                >
                                    Summary
                                </button>
                                <button
                                    className={activeTab === 2 ? 'active' : ''}
                                    onClick={() => setActiveTab(2)}
                                >
                                    Download
                                </button>
                            </div>
                            <div className="notes-button-container">
                                <button className="notes-button ask-ai-button" onClick={openAskAIModal}><i className="fa-solid fa-wand-magic-sparkles notes-icon"></i>Ask AI</button>
                                <button className="notes-button" onClick={openModal}><i className="fa-regular fa-clipboard notes-icon"></i>Note Preferences</button>
                            </div>

                            {isAskAIModalOpen && (
                                <div className="modal-overlay">
                                    <div className="modal-content ask-ai-modal" ref={askAIModalRef}>
                                        <button className="close-modal-button" onClick={closeModal}>×</button>
                                        <h3>ASK AI</h3>
                                        <div className="question-input-container">
                                            <input className="question-input" placeholder="Ask a Question" value={askAIInput} onChange={(event) => setAskAIInput(event.target.value)} onKeyDown={onInputKeyDown}></input>
                                            <button className="submit-button" onClick={onAskAI}>Ask AI</button>
                                        </div>
                                        {aiError && (
                                            <div className="ai-error-message" style={{ color: 'red', marginTop: '10px' }}>
                                                {aiError}
                                            </div>
                                        )}
                                        {!aiAnswerLoading && aiAnswer && aiAnswer.length && 
                                            <div className="answer ai-answer-container">
                                                {aiAnswer.map((line, index) => (
                                                    <div className="ai-answer" key={`ai-answer-${index}`}>{line}</div>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                </div>
                            )}

                            {isModalOpen && (
                                <div className="modal-overlay note-preferences-modal">
                                    <div className="modal-content" ref={notePreferencesModalRef}>
                                        <button className="close-modal-button" onClick={closeModal}>×</button>
                                        <div className="right-column-tab-content">
                                            Coming Soon in v2!
                                        </div>
                                        <img src={notePreferencesImage} alt="note preferences" style={{ width: '400px' }} />
                                    </div>
                                </div>
                            )}
                            {(loading || aiAnswerLoading) &&
                                <div className="spinner-container">
                                    <ClipLoader color={"#123abc"} loading={loading || aiAnswerLoading} size={50} />
                                </div>
                            }
                            {!loading &&
                                <div className="report-container">
                                    {activeTab === 0 &&
                                        <div className="report-text">
                                            <div>
                                                {reportData["report_sections"].map((report, index) => (
                                                    <div key={`report-${index}`}>
                                                        <div className="question">
                                                            <p>{report.question}</p>
                                                            <div className="action-button-container">
                                                                {/* <button className="action-button magic-edit-button"></button> */}
                                                                <button className="action-button copy-button" onClick={() => handleCopy(report.answer, index)} >
                                                                    {copiedIndex === index && (
                                                                        <span
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '-27px',
                                                                                left: '50%',
                                                                                transform: 'translateX(-50%)',
                                                                                backgroundColor: '#333',
                                                                                color: '#fff',
                                                                                padding: '5px',
                                                                                borderRadius: '3px',
                                                                                fontSize: '12px',
                                                                            }}
                                                                        >
                                                                            Copied!
                                                                        </span>
                                                                    )}
                                                                </button>
                                                                <button className="action-button delete-button"></button>
                                                            </div>
                                                        </div>
                                                            <div className="answer">
                                                                {report.answer.map((ans, ind) => (
                                                                    <div key={`report-answer-${index}-${ind}`}>
                                                                        {ans.type === "text" &&
                                                                            <p key={`report-answer-text-${index}-${ind}`}>
                                                                                {formatBoldText(ans.value)}
                                                                            </p>
                                                                        }
                                                                        {ans.type === "list" && 
                                                                            <ul>
                                                                                {ans.items.map((item, i) => (
                                                                                    <li className="pyramid-chart-insight-listitem" key={`report-answer-list-${index}-${ind}-${i}`}>
                                                                                        {formatBoldText(item)}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        }
                                                                    </div>
                                                                ))}
                                                            </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    }
                                    {activeTab === 1 &&
                                        <div className="report-text summary-list">
                                            <h3>{analysisSummaryTitle}</h3>
                                            <ul>
                                                {reportData["summary_sections"].map((summaryItem, index) => {
                                                    if(summaryItem.text.length === 0) return null;

                                                    return(
                                                        <li key={`analysis-summary-heading-${index}`} className="summary-list-item">
                                                            <h4 className="analysis-summary-heading">{formatBoldText(summaryItem.heading)}</h4>
                                                            <ul>
                                                                {summaryItem.text.map((val, ind) => (
                                                                    <li className="pyramid-chart-insight-listitem" key={`analysis-summary-text-${index}-${ind}`} >
                                                                        <p>{formatString(val)}</p>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    }
                                    {activeTab === 2 &&
                                        <div>
                                            {pdfUrl && <PdfViewer pdfUrl={pdfUrl} />}
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                        <div className="analysis-right-column">
                            <div className="analysis-tabs">
                                <button
                                    className={rightActiveTab === 0 ? 'active' : ''}
                                    onClick={() => setRightActiveTab(0)}
                                >
                                    Treatment Plan
                                </button>
                                <button
                                    className={rightActiveTab === 1 ? 'active' : ''}
                                    onClick={() => setRightActiveTab(1)}
                                >
                                    Assist
                                </button>
                            </div>
                            <div>
                                <div className="right-column-tab-content">
                                    Coming Soon in v2!
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div >
        </div >
    );

};

export default Analysis;
